import { useQueuedModal } from '@/hooks/useQueuedModal';
import {
  trackComplimentaryTrialEndedModalClick,
  trackComplimentaryTrialEndedModalViewed,
} from '@/models/analytics/features/complimentary-trial-end-analytics';
import {
  COMPLIMENTARY_ENDED_PLAN_NAMES,
  complimentaryEndedPlanMessages,
  ComplimentaryTrialEndedModal,
  useComplimentaryTrialEndedUsagePercent,
  useComplimentaryTrialEndedVariant,
  type ComplimentaryEndedTarget,
  type ComplimentaryEndedVariant,
  type TrialEndUsageClass,
} from '@heidi/commerce';
import type { ComplimentaryTrialEndedModalAction } from '@heidi/commerce/contracts';
import {
  COMPLIMENTARY_TRIAL_ENDED_MODAL_ID,
  useUserBillingInfo,
} from '@heidi/commerce/data';
import { toast } from '@heidi/design-system/toast';
import { BillingSeatType, ModalViewAction } from '@heidi/schema/ml-scribe';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useOpenClinicianCheckout } from './clinician-upgrade-checkout-route';

const SEAT_BY_TARGET = {
  clinician: { checkout: 'standard', analytics: BillingSeatType.STANDARD },
  max: { checkout: 'max', analytics: BillingSeatType.MAX },
} as const satisfies Record<
  ComplimentaryEndedTarget,
  { checkout: 'standard' | 'max'; analytics: BillingSeatType }
>;

const OpenComplimentaryTrialEndedModal = ({
  variant,
}: {
  variant: ComplimentaryEndedVariant;
}) => {
  const intl = useIntl();
  const { closeWithOptions } = useQueuedModal(COMPLIMENTARY_TRIAL_ENDED_MODAL_ID);
  const openClinicianCheckout = useOpenClinicianCheckout();
  const { data: billingInfo } = useUserBillingInfo();
  const usagePercent = useComplimentaryTrialEndedUsagePercent();
  const currentPlan = billingInfo?.new_product_tier ?? null;
  const currentPlanName = intl.formatMessage(
    COMPLIMENTARY_ENDED_PLAN_NAMES[variant.currentPlan]
  );
  const analyticsContext = useMemo(
    () => ({ currentPlan, usagePercent }),
    [currentPlan, usagePercent]
  );

  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackComplimentaryTrialEndedModalViewed(analyticsContext);
  }, [analyticsContext]);

  const targetSeatType = SEAT_BY_TARGET[variant.target].analytics;
  const trackClick = useCallback(
    (action: ComplimentaryTrialEndedModalAction, label: string) =>
      trackComplimentaryTrialEndedModalClick({
        ...analyticsContext,
        action,
        label,
        targetSeatType,
      }),
    [analyticsContext, targetSeatType]
  );

  const showStayToast = useCallback(
    () =>
      toast({
        tone: 'subtle',
        title: intl.formatMessage(complimentaryEndedPlanMessages.stayToast, {
          planName: currentPlanName,
        }),
      }),
    [currentPlanName, intl]
  );

  const handleUpgrade = useCallback(
    (target: ComplimentaryEndedTarget) => {
      trackClick('upgrade', intl.formatMessage(variant.upgradeLabel));
      closeWithOptions({ action: ModalViewAction.engage });
      openClinicianCheckout({ seat: SEAT_BY_TARGET[target].checkout });
    },
    [closeWithOptions, intl, openClinicianCheckout, trackClick, variant.upgradeLabel]
  );

  const handleStay = useCallback(() => {
    trackClick('stay', `Stay on ${currentPlanName}`);
    closeWithOptions({ action: ModalViewAction.dismiss });
    showStayToast();
  }, [closeWithOptions, currentPlanName, showStayToast, trackClick]);

  // The dialog only reports user dismissals (×, overlay, Escape, drag).
  const handleDismiss = useCallback(() => {
    trackClick('close', 'close');
    closeWithOptions({ action: ModalViewAction.dismiss });
    showStayToast();
  }, [closeWithOptions, showStayToast, trackClick]);

  return (
    <ComplimentaryTrialEndedModal
      open
      variant={variant}
      onUpgrade={handleUpgrade}
      onStay={handleStay}
      onDismiss={handleDismiss}
    />
  );
};

const QueuedComplimentaryTrialEndedModal = () => {
  const variant = useComplimentaryTrialEndedVariant();
  if (variant == null) return null;
  // Keyed so a different variant opens on its own preselected tab.
  return <OpenComplimentaryTrialEndedModal key={variant.id} variant={variant} />;
};

export const ComplimentaryTrialEndedModalHost = ({
  usageClass,
}: {
  usageClass: TrialEndUsageClass | null;
}) => {
  const { isOpen } = useQueuedModal(COMPLIMENTARY_TRIAL_ENDED_MODAL_ID);

  if (!isOpen || usageClass == null) return null;

  return <QueuedComplimentaryTrialEndedModal />;
};
